import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "354",
  "slug": "most-isolated-inhabited-places-on-earth",
  "title": "The Most Isolated Inhabited Places on Earth",
  "excerpt": "Some communities live thousands of kilometers from the nearest town — on volcanic specks in the open ocean, deep in canyons, or in the coldest corners of Siberia. Here are the most remote places people actually call home.",
  "date": "2026-06-05",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxyZW1vdGUlMjBpc2xhbmQlMjBvY2VhbnxlbnwxfDB8fHwxNzgwNjQ3MTQyfDA&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "There is a difference between remote and uninhabited. Antarctica is empty most of the year; the open Pacific is mostly water. The places in this list are different — they have permanent residents, post boxes, and someone who keeps the lights on, yet they sit further from the rest of humanity than almost anywhere else on Earth. Reaching them can take a boat that comes a few times a year, a mule down a canyon wall, or a flight that depends entirely on the weather."
    },
    {
      "type": "paragraph",
      "text": "These are the kinds of locations that make a satellite-guessing game genuinely hard: a tiny smudge of land in a vast field of ocean blue, with almost no human pattern to give it away."
    },
    {
      "type": "heading",
      "text": "Tristan da Cunha: The Loneliest Island"
    },
    {
      "type": "paragraph",
      "text": "Tristan da Cunha, a British territory in the South Atlantic, is usually called the most remote inhabited island in the world. Its nearest inhabited neighbor, Saint Helena, lies roughly 2,400 kilometers away, and the South African coast is further still. A few hundred people live in the island's single settlement — charmingly named Edinburgh of the Seven Seas — at the foot of a volcano. There is no airstrip; the only way in is a multi-day sea voyage."
    },
    {
      "type": "heading",
      "text": "Pitcairn: The Mutineers' Descendants"
    },
    {
      "type": "paragraph",
      "text": "In the South Pacific, the Pitcairn Islands are home to one of the smallest permanent populations of any jurisdiction on Earth — only a few dozen residents, many descended from the Bounty mutineers who settled there in 1790. There is no airport. Visitors and supplies arrive by sea, and the journey involves a long boat trip from French Polynesia followed by a transfer through often rough surf."
    },
    {
      "type": "heading",
      "text": "Easter Island: A Speck With Giants"
    },
    {
      "type": "paragraph",
      "text": "Rapa Nui, better known as Easter Island, sits roughly 3,500 kilometers off the coast of Chile, to which it belongs. Famous for its moai statues, it is one of the most isolated inhabited islands with a substantial population and a working airport. Even so, its nearest inhabited neighbor is far over the horizon, and everything from fuel to fresh food arrives across a very long stretch of empty ocean."
    },
    {
      "type": "heading",
      "text": "Ittoqqortoormiit: Greenland's Edge"
    },
    {
      "type": "paragraph",
      "text": "Not all isolation is tropical. Ittoqqortoormiit, on the east coast of Greenland, is one of the most remote inhabited settlements in the Arctic. The sea around it is frozen for much of the year, the nearest towns are hundreds of kilometers away, and access depends on a small airport and a short summer shipping window. From above it is a tiny cluster of brightly painted houses against an immense white-and-grey coastline."
    },
    {
      "type": "heading",
      "text": "Supai: Mail by Mule"
    },
    {
      "type": "paragraph",
      "text": "Isolation does not require an ocean. Supai, the village of the Havasupai people deep inside a side canyon of the Grand Canyon in Arizona, has no road access at all. Residents and supplies travel in and out on foot, by mule, or by helicopter, and it is often described as the last place in the United States where mail is still delivered by pack animal. The green waterfalls nearby are spectacular; the eight-mile trail down to them is the only way in."
    },
    {
      "type": "heading",
      "text": "The Cold Poles of Siberia"
    },
    {
      "type": "paragraph",
      "text": "In northeastern Russia, the towns of Oymyakon and Verkhoyansk compete for the title of the coldest permanently inhabited place on Earth, with winter temperatures that have plunged toward minus 60 degrees Celsius and below. They are remote not just in distance but in conditions — reaching them means long drives over ice roads through some of the emptiest land on the planet."
    },
    {
      "type": "heading",
      "text": "More Far-Flung Outposts"
    },
    {
      "type": "paragraph",
      "text": "The list runs long. La Rinconada, high in the Peruvian Andes at around 5,000 meters, is often called the highest permanent settlement on Earth — a gold-mining town clinging to a glacier where thin air and brutal cold keep it cut off from easy access. Out in the Indian Ocean, the Yemeni island of Socotra is so isolated that a large share of its plants grow nowhere else, including the otherworldly dragon's blood trees. And Palmerston Island, a coral atoll in the Cook Islands whose residents largely descend from a single 19th-century settler, is reached only by a ship that calls a handful of times a year."
    },
    {
      "type": "heading",
      "text": "What Isolation Looks Like From Space"
    },
    {
      "type": "paragraph",
      "text": "Seen from a satellite, these places share a signature: a minute fingerprint of human activity surrounded by overwhelming nature. A few straight lines of a settlement, a single pale airstrip, maybe a harbor — and then nothing but ocean, ice, desert, or canyon for as far as the image stretches. That contrast is exactly what makes them so satisfying, and so difficult, to identify."
    }
  ]
};
